import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import { component } from 'picoapp';
import { useMount, useUpdateEffect } from 'ahooks';
import { useProduct, useProductSpecs, useStyleSpecs } from '@hooks';
import './component.scss';
import ProductForm from './ProductForm';
import ProductMainHeader from './ProductMainHeader';
import { baseProductSpecs, baseProductState, calculationSpecs } from '@state';
import ContentLoader from 'react-content-loader';
import { motion, AnimatePresence } from 'framer-motion';
import { getApplicationTags } from '@utils';
import classNames from 'classnames';

var slugify = require('slugify');

import ProductGallery from './ProductGallery';
import ProductInfo from './ProductInfo';
import ProductBreadcrumbs from './ProductBreadcrumbs';
import BundleUpsell from './BundleUpsell';
import WarrantyWidget from './WarrantyWidget';

const MyLoader = () => (
  <ContentLoader viewBox="0 0 1017 567">
    <path
      d="M664,0 L664,567 L97,567 L97,0 L664,0 Z M88,291 L88,379 L0,379 L0,291 L88,291 Z M88,194 L88,282 L0,282 L0,194 L88,194 Z M1014,215 C1015.65685,215 1017,216.343146 1017,218 C1017,219.656854 1015.65685,221 1014,221 L761,221 C759.343146,221 758,219.656854 758,218 C758,216.343146 759.343146,215 761,215 L1014,215 Z M1014,197 C1015.65685,197 1017,198.343146 1017,200 C1017,201.656854 1015.65685,203 1014,203 L761,203 C759.343146,203 758,201.656854 758,200 C758,198.343146 759.343146,197 761,197 L1014,197 Z M88,97 L88,185 L0,185 L0,97 L88,97 Z M1014,179 C1015.65685,179 1017,180.343146 1017,182 C1017,183.656854 1015.65685,185 1014,185 L761,185 C759.343146,185 758,183.656854 758,182 C758,180.343146 759.343146,179 761,179 L1014,179 Z M886.214286,111 C890.356421,111 893.714286,114.357864 893.714286,118.5 C893.714286,122.642136 890.356421,126 886.214286,126 L765.5,126 C761.357864,126 758,122.642136 758,118.5 C758,114.357864 761.357864,111 765.5,111 L886.214286,111 Z M88,0 L88,88 L0,88 L0,0 L88,0 Z M937.5,58 C943.29899,58 948,62.7010101 948,68.5 C948,74.2989899 943.29899,79 937.5,79 L768.5,79 C762.70101,79 758,74.2989899 758,68.5 C758,62.7010101 762.70101,58 768.5,58 L937.5,58 Z"
      id="Combined-Shape"
      fill="#F3F3F3"
    ></path>
  </ContentLoader>
);

const queryClient = new QueryClient();

const ProductMainWrapper = ({ node, ctx }) => {
  const [handle, setHandle] = useState(null);
  useMount(() => {
    setHandle(node.dataset.handle);
  });
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <ProductMain handle={handle} ctx={ctx} node={node}></ProductMain>
      </QueryClientProvider>
    </RecoilRoot>
  );
};

const ProductMain = (props) => {
  const { handle, ctx, node } = props;
  const [mainProdApplication, setMainProdApplication] = useState();
  const { isLoading, error, data: product, isFetching } = useProduct(handle);
  const persistBaseProduct = useSetRecoilState(baseProductState);
  const { data: specsData } = useStyleSpecs(product?.id);
  const { data: productSpecs } = useProductSpecs(handle);
  const setSpecs = useSetRecoilState(calculationSpecs);
  const setProdSpecs = useSetRecoilState(baseProductSpecs);

  useMount(() => {
    if (node.dataset.customer) {
      ctx.hydrate({ customer_email: node.dataset.customer });
    }
    const interval = setInterval(function () {
      if (window.Yotpo && window.yotpo) {
        var api = new Yotpo.API(yotpo);
        api.refreshWidgets();
        clearInterval(interval);
      }
    }, 1000);
  });

  // Persist base product to app state
  useUpdateEffect(() => {
    if (product) {
      persistBaseProduct(product);
      // setIsOrganizer(product.tags?.find((item) => item.includes('room')) !== undefined);
      let tags = getApplicationTags(product.tags, 'application');
      if (tags) {
        setMainProdApplication(tags[0]);
      }
    }
  }, [product]);

  useUpdateEffect(() => {
    if (specsData) {
      setSpecs(specsData);
    }
  }, [specsData]);

  useUpdateEffect(() => {
    if (productSpecs) {
      setProdSpecs(productSpecs);
    }
  }, [productSpecs]);

  let wrapperClasses = classNames('ProductMain', {
    isCustom: Boolean(specsData),
  });

  if (product?.tags.includes('BTO')) {
    let title_parts = product.title.split('(');
    let baseSlug = slugify(title_parts[0], { lower: true });
    window.location.href = `${window.location.origin}/products/${baseSlug}`;
    return null;
  }

  return (
    <AnimatePresence exitBeforeEnter>
      {product ? (
        <motion.div className={wrapperClasses} key="product-main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 1 }}>
          <ProductMainHeader product={product}></ProductMainHeader>
          <ProductGallery handle={handle} product={product}></ProductGallery>
          <div className="ProductMain__options">
            <div className="inner">
              <ProductBreadcrumbs product={product} />
              <h1>{product.title}</h1>
              {/* <div className="yotpo bottomLine u-show--fromTablet" data-product-id={product.id}></div> */}
              <ProductForm product={product} ctx={ctx}></ProductForm>
              <WarrantyWidget></WarrantyWidget>
              <ProductInfo {...props} product={product} specsData={specsData} mainProdApplication={mainProdApplication}></ProductInfo>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div key="product-main-loader" initial={{ opacity: 1 }} animate={{ opacity: 0 }} exit={{ opacity: 0 }}>
          <MyLoader></MyLoader>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default component((node, ctx) => {
  const element = <ProductMainWrapper ctx={ctx} node={node} />;
  ReactDOM.render(element, node);

  // Unmount component on Pjax navigation
  return function () {
    try {
      ReactDOM.unmountComponentAtNode(node);
    } catch (e) {
      console.error(e);
    }
  };
});
